/**
 * research.ts — labs generate research points; points buy tech tree nodes.
 */
import type { GameState } from '../core/GameState';
import type { EventBus } from '../core/EventBus';
import { safeNumber } from '../core/numbers';
import { TECH_BY_ID } from '../data';
import type { Derived } from './derived';
import { meetsRequirement } from './unlock';

export function tickResearch(state: GameState, dtMs: number, d: Derived): void {
  if (d.researchRate <= 0) return;
  state.research.points = safeNumber(state.research.points + d.researchRate * (dtMs / 1000));
}

/** Whether a tech is available to research now (deps + requirements met, not owned). */
export function canResearch(state: GameState, techId: string): boolean {
  const t = TECH_BY_ID[techId];
  if (!t || state.research.researched[techId]) return false;
  if (!meetsRequirement(state, t.requires)) return false;
  if (state.research.points < t.cost) return false;
  return t.deps.every((dep) => state.research.researched[dep]);
}

/** Whether a tech's prerequisites (deps + requirements) are met, ignoring cost. */
export function isTechUnlocked(state: GameState, techId: string): boolean {
  const t = TECH_BY_ID[techId];
  if (!t) return false;
  if (!meetsRequirement(state, t.requires)) return false;
  return t.deps.every((dep) => state.research.researched[dep]);
}

export function researchTech(state: GameState, techId: string, bus?: EventBus): boolean {
  if (!canResearch(state, techId)) return false;
  const t = TECH_BY_ID[techId]!;
  state.research.points -= t.cost;
  state.research.researched[techId] = true;
  bus?.emit('tech:researched', { techId });
  bus?.emit('notify', { kind: 'success', message: `Erforscht: ${t.name}` });
  return true;
}
